'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { MouseEvent } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import WaveDivider from '@/components/WaveDivider';
import DoubleWaveDivider from '@/components/DoubleWaveDivider';
import styles from './page.module.scss';

export default function CafeerContent() {
  const { t } = useTranslation();
  const scrollToPricing = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const target = document.getElementById('pricing');
    if (!target) {
      return;
    }

    const startY = window.scrollY;
    const targetY = target.getBoundingClientRect().top + window.scrollY;
    const distance = targetY - startY;
    const duration = 1400;
    const startTime = performance.now();

    const easeInOutCubic = (x: number) => (
      x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
    );

    const step = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      window.scrollTo(0, startY + distance * easedProgress);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  };

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroImageWrap}>
          <Image
            src="/media/site/roastery-1.webp"
            alt={t.cafeer.heroAlt}
            fill
            priority
            style={{ objectFit: 'cover' }}
          />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            {t.cafeer.heroTitle.split('\n').map((line, i) => (
              <span key={i}>{i > 0 && <br />}{line}</span>
            ))}
          </h1>
          <p className={styles.heroSub}>
            {t.cafeer.heroSubtitle}
          </p>
          <a
            href="#pricing"
            className={styles.heroCta}
            onClick={scrollToPricing}
          >
            Få et godt tilbud
          </a>
        </div>
      </section>

      <div className={styles.heroWave}>
        <WaveDivider from="yellow" />
      </div>

      <section className={`${styles.intro} section section--magenta`}>
        <div className="container">
          <div className={styles.introContent}>
            <h2 className={styles.sectionTitle}>
              {t.cafeer.introTitle}
            </h2>
            <p className={styles.introLead}>
              {t.cafeer.introP1}
            </p>
            <p>
              {t.cafeer.introP2}
            </p>
            <p>
              {t.cafeer.introP3}
            </p>
          </div>
        </div>
      </section>

      <DoubleWaveDivider flip reverse />

      <section className={styles.benefits}>
        <div className={styles.benefitsImageWrap}>
          <Image
            src="/media/site/roastery-2.webp"
            alt={t.cafeer.benefitsImageAlt}
            fill
            style={{ objectFit: 'cover' }}
          />
          <div className={styles.benefitsOverlay} />
        </div>
        <div className={`container ${styles.benefitsInner}`}>
          <h2 className={styles.sectionTitle}>{t.cafeer.benefitsTitle}</h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 4L29 14L40 16L32 24L34 35L24 30L14 35L16 24L8 16L19 14L24 4Z" fill="currentColor" />
                </svg>
              </div>
              <h3>{t.cafeer.benefit1Title}</h3>
              <p>{t.cafeer.benefit1Body}</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 8H36V36H8V8Z" fill="none" stroke="currentColor" strokeWidth="3" />
                  <path d="M36 16H40C42.2 16 44 17.8 44 20V28C44 30.2 42.2 32 40 32H36" stroke="currentColor" strokeWidth="3" />
                  <circle cx="16" cy="42" r="3" fill="currentColor" />
                  <circle cx="28" cy="42" r="3" fill="currentColor" />
                </svg>
              </div>
              <h3>{t.cafeer.benefit2Title}</h3>
              <p>{t.cafeer.benefit2Body}</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 4C13 4 4 13 4 24C4 35 13 44 24 44C35 44 44 35 44 24C44 13 35 4 24 4Z" fill="none" stroke="currentColor" strokeWidth="3" />
                  <path d="M16 24L22 30L34 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
              <h3>{t.cafeer.benefit3Title}</h3>
              <p>{t.cafeer.benefit3Body}</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 4V24L34 34" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="3" />
                </svg>
              </div>
              <h3>{t.cafeer.benefit4Title}</h3>
              <p>{t.cafeer.benefit4Body}</p>
            </div>
          </div>
        </div>
      </section>

      <DoubleWaveDivider />

      <section className={`${styles.howItWorks} section section--magenta`}>
        <div className="container">
          <h2 className={styles.sectionTitle}>{t.cafeer.howTitle}</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.step}>
              <span className={styles.stepNumber}>01</span>
              <h3>{t.cafeer.howStep1Title}</h3>
              <p>{t.cafeer.howStep1Body}</p>
            </div>
            <div className={styles.step}>
              <span className={styles.stepNumber}>02</span>
              <h3>{t.cafeer.howStep2Title}</h3>
              <p>{t.cafeer.howStep2Body}</p>
            </div>
            <div className={styles.step}>
              <span className={styles.stepNumber}>03</span>
              <h3>{t.cafeer.howStep3Title}</h3>
              <p>{t.cafeer.howStep3Body}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className={`${styles.pricing} section section--magenta`}>
        <div className="container">
          <div className={styles.pricingGrid}>
            <div className={styles.pricingContent}>
              <h2 className={styles.sectionTitle}>{t.cafeer.pricingTitle}</h2>
              <p>{t.cafeer.pricingP1}</p>
              <p>{t.cafeer.pricingP2}</p>
              <div className={styles.pricingHighlight}>
                <div className={styles.pricingPrice}>
                  <span className={styles.pricingFrom}>{t.cafeer.pricingFrom}</span>
                  <span className={styles.pricingAmountRow}>
                    <span className={styles.pricingAmount}>{t.cafeer.pricingAmount}</span>
                    <span className={styles.pricingPer}>{t.cafeer.pricingPer}</span>
                  </span>
                </div>
                <div className={styles.pricingCta}>
                  <a href={`tel:${t.cafeer.ctaPhone.replace(/\s/g, '')}`} className={styles.pricingCtaLink}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    <span className={styles.pricingCtaDetail}>{t.cafeer.ctaPhone}</span>
                  </a>
                  <a href={`mailto:${t.cafeer.ctaEmail}?subject=Cafékaffe`} className={styles.pricingCtaLink}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <span className={styles.pricingCtaDetail}>{t.cafeer.ctaEmail}</span>
                  </a>
                </div>
              </div>
            </div>
            <div className={styles.pricingVideo}>
              <video
                src="/media/site/coffeebryg.mp4"
                autoPlay
                loop
                muted
                playsInline
                aria-label={t.cafeer.pricingImageAlt}
              />
            </div>
          </div>
        </div>
      </section>

      <WaveDivider from="magenta" />

      <section className={`${styles.cta} section section--yellow`}>
        <div className="container">
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              {t.cafeer.ctaTitle}
            </h2>
            <p className={styles.ctaSub}>
              {t.cafeer.ctaBody}
            </p>
            <div className={styles.ctaButtons}>
              <a href={`mailto:${t.cafeer.ctaEmail}?subject=Cafékaffe`} className={styles.ctaBtn}>
                {t.cafeer.ctaContact}
              </a>
              <Link href="/shop" className={styles.ctaBtnOutline}>
                {t.cafeer.ctaShop}
              </Link>
            </div>
            <p className={styles.ctaNote}>
              {t.cafeer.ctaNote}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
